import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiBook, FiUsers, FiUser, FiLogOut, FiPlus, FiMenu } from 'react-icons/fi';
import Avatar from './Avatar';

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar on navigation for mobile devices
  useEffect(() => {
    const closeSidebarOnNavigate = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };
    closeSidebarOnNavigate();
  }, [location.pathname]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiHome },
    { path: '/classrooms', label: 'Classrooms', icon: FiUsers },
    { path: '/quizzes', label: 'Quizzes', icon: FiBook },
    { path: '/profile', label: 'Profile', icon: FiUser },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 bg-white shadow-lg transition-transform transform z-30 overflow-hidden
          ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0'}
          md:translate-x-0 md:w-64`}
        // aria-hidden={!sidebarOpen}
        {...(!sidebarOpen ? { inert: '' } : {})}
      >
        <div className="flex items-center justify-between h-16 bg-primary-600 px-4">
          <h1 className={`text-xl font-bold text-white truncate transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            Quiz Chakra 📃
          </h1>
          <button
            onClick={toggleSidebar}
            className="text-white md:hidden focus:outline-none"
            aria-label="Toggle sidebar"
          >
            <FiMenu size={24} />
          </button>
        </div>

        <nav className="mt-8">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setSidebarOpen(false);
                  }
                }}
                className={`flex items-center px-6 py-3 transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-600 border-r-4 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="mr-3" size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {isAdmin && (
          <div className="mt-8 px-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Admin Actions
            </h3>
            <Link
              to="/quizzes/create"
              onClick={() => {
                if (window.innerWidth < 768) {
                  setSidebarOpen(false);
                }
              }}
              className="mt-3 flex items-center text-gray-700 hover:text-primary-600"
            >
              <FiPlus className="mr-2" size={16} />
              Create Quiz
            </Link>
            <Link
              to="/classrooms/create"
              onClick={() => {
                if (window.innerWidth < 768) {
                  setSidebarOpen(false);
                }
              }}
              className="mt-2 flex items-center text-gray-700 hover:text-primary-600"
            >
              <FiPlus className="mr-2" size={16} />
              Create Classroom
            </Link>
          </div>
        )}

        <div className="absolute bottom-0 w-full p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Avatar name={user?.name} size="medium" className="mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-600 transition-colors"
            >
              <FiLogOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile when sidebar is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-20 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main
        className={`flex-1 transition-margin duration-300 ease-in-out p-8
          ${sidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}
      >
        {/* Toggle button for mobile */}
        <button
          onClick={toggleSidebar}
          className="mb-4 text-gray-700 md:hidden focus:outline-none"
          aria-label="Toggle sidebar"
        >
          <FiMenu size={24} />
        </button>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
